import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, FileText, Inbox } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { cn } from '../../lib/cn';
import { formatHebrewRelative } from '../../lib/relativeTime';
import { DocumentDrawer } from './DocumentDrawer';
import {
  DOC_TYPES,
  DOC_TYPE_LABELS,
  entityPath,
  useDocuments,
  useEntityNames,
  type DocumentRow,
  type DocumentType,
} from './documentsQueries';
import styles from './documents.module.css';

type Filter = DocumentType | 'all';

const SNIPPET_LENGTH = 150;

function snippet(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  if (flat.length <= SNIPPET_LENGTH) return flat;
  return `${flat.slice(0, SNIPPET_LENGTH)}…`;
}

export function DocumentsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: docs = [], isLoading, error } = useDocuments(orgId);
  const { data: entityNames = {} } = useEntityNames(orgId, docs);

  const [filter, setFilter] = useState<Filter>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const acc: Record<DocumentType, number> = {
      idea: 0,
      proposal_idea: 0,
      meeting_note: 0,
      note: 0,
      sop: 0,
    };
    for (const doc of docs) acc[doc.type] += 1;
    return acc;
  }, [docs]);

  const visible = useMemo(
    () => (filter === 'all' ? docs : docs.filter((doc) => doc.type === filter)),
    [docs, filter],
  );

  const openDoc = docs.find((doc) => doc.id === openId) ?? null;
  const openEntityName = openDoc?.entity_id ? (entityNames[openDoc.entity_id] ?? null) : null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <h1>מסמכים</h1>
          <p className={styles.pageSub}>
            כל מה שנקלט ואינו משימה. הבוט כותב לכאן ישירות מ-WhatsApp.
          </p>
        </div>
        <FileText size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      {error ? (
        <p className={styles.notice}>
          לא הצלחנו לטעון מסמכים. {error instanceof Error ? error.message : ''}
        </p>
      ) : null}

      <div className={styles.filterBar} role="group" aria-label="סינון לפי סוג">
        <button
          type="button"
          className={cn(styles.chip, filter === 'all' && styles.chipActive)}
          aria-pressed={filter === 'all'}
          onClick={() => {
            setFilter('all');
          }}
        >
          הכל <span className="num">{docs.length}</span>
        </button>
        {DOC_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className={cn(styles.chip, filter === type && styles.chipActive)}
            aria-pressed={filter === type}
            onClick={() => {
              setFilter(type);
            }}
          >
            {DOC_TYPE_LABELS[type]} <span className="num">{counts[type]}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className={styles.notice}>טוען…</p>
      ) : visible.length === 0 ? (
        <EmptyState filtered={filter !== 'all'} />
      ) : (
        <div className={styles.table} role="table" aria-label="מסמכים">
          <div className={cn(styles.rowGrid, styles.headRow)} role="row">
            <div role="columnheader">מסמך</div>
            <div role="columnheader">סוג</div>
            <div role="columnheader">משויך ל</div>
            <div role="columnheader">נוצר</div>
            <div role="columnheader" />
          </div>

          {visible.map((doc) => (
            <DocumentRowItem
              key={doc.id}
              doc={doc}
              entityName={doc.entity_id ? (entityNames[doc.entity_id] ?? null) : null}
              onOpen={() => {
                setOpenId(doc.id);
              }}
            />
          ))}
        </div>
      )}

      <DocumentDrawer
        open={openDoc !== null}
        doc={openDoc}
        entityName={openEntityName}
        orgId={orgId}
        onClose={() => {
          setOpenId(null);
        }}
      />
    </div>
  );
}

function DocumentRowItem({
  doc,
  entityName,
  onOpen,
}: {
  doc: DocumentRow;
  entityName: string | null;
  onOpen: () => void;
}) {
  const link = entityPath(doc.entity_type, doc.entity_id);
  const body = snippet(doc.body);

  return (
    <div
      className={styles.rowGrid}
      role="row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className={styles.titleCell}>
        <strong>{doc.title}</strong>
        {body ? <small>{body}</small> : null}
      </span>
      <span className={cn(styles.typeBadge, styles[doc.type])}>{DOC_TYPE_LABELS[doc.type]}</span>
      <span className={styles.entityCell}>
        {entityName === null ? <span className={styles.dim}>—</span> : null}
        {entityName !== null && link === null ? entityName : null}
        {entityName !== null && link !== null ? (
          <Link
            to={link}
            className={styles.entityLink}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {entityName}
          </Link>
        ) : null}
      </span>
      <span className={styles.timeCell}>
        <span className="num">{formatHebrewRelative(doc.created_at)}</span>
      </span>
      <ChevronLeft size={16} className={styles.chevron} aria-hidden="true" />
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className={styles.emptyCard}>
      <span className={styles.emptyIcon}>
        <Inbox size={36} aria-hidden="true" />
      </span>
      {filtered ? (
        <>
          <h2>אין מסמכים מהסוג הזה</h2>
          <p>בחר סוג אחר, או לחץ "הכל".</p>
        </>
      ) : (
        <>
          <h2>כאן יושב כל מה שאינו משימה</h2>
          <p>רעיון, הצעה ללקוח, סיכום פגישה, הערה או נוהל.</p>
          <p>שלח את זה לבוט ב-WhatsApp. הוא יסווג ויפתח כאן מסמך, משויך ללקוח.</p>
        </>
      )}
    </div>
  );
}
